/**
 * `id` of the `<template>` that holds the page's inert module preloads: `BOOT_PRELOAD_ID` in
 * `prerender/markers.ts`, repeated here so that this module — the whole of the entry chunk's logic — imports
 * nothing at all (see below); `boot-gate.test.ts` fails if the two ever differ.
 */
const BOOT_PRELOAD_TEMPLATE_ID = 'sbg-boot-preload';

/**
 * When the app starts. This module is the whole of the entry chunk's logic, so it imports nothing:
 * everything else (React, the router, the shell) is fetched only once this decides it is time. (A module the
 * entry shares with the app would be pulled into the app's chunk by the bundler and drag the whole chunk into
 * the first load: `vite.config.ts` groups everything `app/boot.tsx` imports into the `shell` chunk.)
 *
 * A pre-rendered page is readable and styled from its HTML and CSS alone. Starting its JavaScript at once would
 * have ~140 kB of scripts share a slow link with the stylesheet and the hero image and put a long hydration task
 * in front of the first paint. So the app starts after the page has *loaded* (the `load` event, which waits for
 * the hero image) and *painted* (the browser has reported its first contentful paint), plus a short calm period
 * for the largest element to settle. The page is not interactive during that gap — no scripts have run yet:
 * links work as links — and it is short compared with the time the browser would have spent fighting for
 * bandwidth and the main thread.
 */

/** A stalled `load` (one slow image) or a background tab that never paints must not hold the app back for ever. */
export const BOOT_MAX_WAIT_MS = 4000;

/**
 * After both `load` and the first contentful paint, wait this long before starting: the browser decides the
 * largest contentful paint within a few frames of the first (the hero headline starts at once, see
 * `hero.css`), so hydration never lands between the first paint and the LCP.
 */
export const PAINT_SETTLE_MS = 250;

/** Calls `onPaint` once the browser has reported the first contentful paint (never, in a background tab). */
function onFirstContentfulPaint(onPaint: () => void): void {
  const supported =
    typeof PerformanceObserver !== 'undefined' &&
    PerformanceObserver.supportedEntryTypes.includes('paint');
  if (!supported) {
    onPaint(); // no way to ask: the frame after `load` stands in for it
    return;
  }
  try {
    const observer = new PerformanceObserver((list) => {
      if (list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
        observer.disconnect();
        onPaint();
      }
    });
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    onPaint();
  }
}

/**
 * Run `start` once, when the page has loaded, painted and settled — or after `maxWaitMs`, whichever comes
 * first. Never runs it twice.
 */
export function afterFirstPaint(
  start: () => void,
  maxWaitMs: number = BOOT_MAX_WAIT_MS,
  settleMs: number = PAINT_SETTLE_MS,
): void {
  let started = false;
  const once = (): void => {
    if (started) return;
    started = true;
    start();
  };
  window.setTimeout(once, maxWaitMs);

  let loaded = false;
  let painted = false;
  const settle = (): void => {
    if (loaded && painted) window.setTimeout(once, settleMs);
  };

  const afterLoad = (): void => {
    // the frame after `load`: whatever the load put on screen has been drawn by the time this runs
    window.requestAnimationFrame(() => {
      loaded = true;
      settle();
    });
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });

  onFirstContentfulPaint(() => {
    painted = true;
    settle();
  });
}

/**
 * Make the page's `<link rel="modulepreload">` tags live: the pre-render keeps them inert in a `<template>`
 * (see `BOOT_PRELOAD_ID`) so nothing is fetched while the page is still being painted. Called right before the
 * app's own `import()`, so the whole module graph is fetched in one parallel wave.
 */
export function startPreloads(): void {
  const template = document.getElementById(BOOT_PRELOAD_TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return;
  document.head.append(template.content.cloneNode(true));
  template.remove();
}
